import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/apiClient';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Loader2, 
  Video, 
  Copy, 
  Calendar,
  CheckCircle2,
  Eye,
  X,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';

const ScriptGeneratorNew = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('generate'); // 'generate' or 'history'
  const [loading, setLoading] = useState(false);
  const [activeBusiness, setActiveBusiness] = useState(null);
  
  // Form data
  const [topic, setTopic] = useState('problem');
  const [videoContext, setVideoContext] = useState('');
  const [duration, setDuration] = useState('30');
  const [style, setStyle] = useState('hormozi');
  
  // Generated result
  const [scriptResult, setScriptResult] = useState(null);
  
  // History
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Detail modal
  const [selectedScript, setSelectedScript] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    loadBusiness();
    loadHistory();
  }, []);

  const loadBusiness = async () => {
    try {
      const response = await apiClient.get('/user/active-business');
      setActiveBusiness(response.data.business);
    } catch (error) {
      console.error('Error loading business:', error);
    }
  };

  const loadHistory = async () => {
    try {
      setLoadingHistory(true);
      const response = await apiClient.get('/scripts');
      setHistory(response.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch (error) {
      console.error('Error loading history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    
    if (!activeBusiness) {
      toast.error('Primero selecciona o crea un negocio');
      return;
    }
    
    if (!videoContext.trim()) {
      toast.error('Describe de qué quieres que trate el video');
      return;
    }
    
    try {
      setLoading(true);
      const response = await apiClient.post('/scripts/generate', {
        business_id: activeBusiness.id,
        duration: parseInt(duration),
        style,
        purpose: topic,
        context: videoContext
      });
      
      setScriptResult(response.data);
      toast.success('¡Script generado!');
      loadHistory();
      
    } catch (error) {
      console.error('Error generating script:', error);
      toast.error(error.response?.data?.detail || 'Error al generar script');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (text) => {
    try {
      // Try modern Clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        toast.success('Copiado al portapapeles');
      } else {
        // Fallback for non-secure contexts
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          document.execCommand('copy');
          toast.success('Copiado al portapapeles');
        } catch (err) {
          toast.error('No se pudo copiar. Intenta seleccionar el texto manualmente.');
        }
        
        document.body.removeChild(textArea);
      }
    } catch (err) {
      // Final fallback
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        document.execCommand('copy');
        toast.success('Copiado al portapapeles');
      } catch (execErr) {
        toast.error('No se pudo copiar. Intenta seleccionar el texto manualmente.');
      }
      
      document.body.removeChild(textArea);
    }
  };

  const renderGenerateTab = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Generar Script de Video</CardTitle>
          <CardDescription>
            Scripts profesionales para TikTok, Reels, YouTube y más
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleGenerate} className="space-y-6">
            {activeBusiness && (
              <div className="p-3 bg-blue-50 rounded-lg text-sm">
                <span className="text-blue-800">
                  Generando para: <strong>{activeBusiness.business_name || activeBusiness.business_description?.substring(0, 30)}</strong>
                </span>
              </div>
            )}
            
            <div>
              <Label htmlFor="videoContext">¿De qué quieres que trate el video? *</Label>
              <Textarea
                id="videoContext"
                value={videoContext}
                onChange={(e) => setVideoContext(e.target.value)}
                placeholder="Ej: Cómo nuestro software de gestión ayuda a los dueños de restaurantes a reducir el desperdicio de comida en un 40%"
                rows={3}
                className="mt-1"
                required
              />
              <p className="text-xs text-slate-500 mt-1">
                Describe el tema específico, mensaje clave o historia que quieres contar
              </p>
            </div>

            <div>
              <Label htmlFor="topic">Propósito del Video *</Label>
              <select
                id="topic"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                required
              >
                <option value="problem">Problema - Exponer un dolor del cliente</option>
                <option value="solution">Solución - Mostrar cómo resuelves el problema</option>
                <option value="testimonial">Testimonial - Historia de éxito</option>
                <option value="educational">Educativo - Enseñar algo de valor</option>
                <option value="authority">Autoridad - Demostrar expertise</option>
                <option value="comparison">Comparación - Antes vs Después</option>
                <option value="opportunity">Oportunidad - Mostrar el potencial</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="duration">Duración (segundos) *</Label>
                <select
                  id="duration"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                >
                  <option value="10">10 seg - TikTok Quick</option>
                  <option value="15">15 seg - Reels</option>
                  <option value="30">30 seg - Stories</option>
                  <option value="60">60 seg - YouTube Shorts</option>
                  <option value="90">90 seg - Largo</option>
                </select>
              </div>

              <div>
                <Label htmlFor="style">Estilo de Persuasión *</Label>
                <select
                  id="style"
                  value={style}
                  onChange={(e) => setStyle(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                >
                  <option value="sinek">Inspiracional - Propósito y emoción</option>
                  <option value="hormozi">Lógico - Valor claro y directo</option>
                  <option value="belfort">Persuasivo - Urgencia y acción</option>
                </select>
              </div>
            </div>

            <Button type="submit" className="w-full h-12" disabled={loading || !activeBusiness}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                  Generando script...
                </>
              ) : (
                <>
                  <Video className="mr-2 w-5 h-5" />
                  Generar Script
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {scriptResult && (
        <div className="space-y-6">
          <Card className="border-2 border-green-300">
            <CardHeader className="bg-green-50">
              <div className="flex items-center justify-between">
                <CardTitle>Script Generado</CardTitle>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleCopy(scriptResult.script_text || '')}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copiar Todo
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded">
                    {scriptResult.duration}s
                  </span>
                  <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs font-semibold rounded capitalize">
                    {scriptResult.style}
                  </span>
                  <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded capitalize">
                    {scriptResult.purpose}
                  </span>
                </div>

                <div className="bg-white p-4 rounded-lg border">
                  <h4 className="font-semibold text-sm text-slate-700 mb-2">📝 Script Completo:</h4>
                  <p className="text-slate-900 whitespace-pre-wrap">{scriptResult.script_text}</p>
                </div>

                {scriptResult.scene_descriptions && scriptResult.scene_descriptions.length > 0 && (
                  <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                    <h4 className="font-semibold text-sm text-purple-900 mb-2">🎬 Descripciones de Escenas:</h4>
                    <ul className="space-y-2">
                      {scriptResult.scene_descriptions.map((scene, idx) => (
                        <li key={idx} className="text-sm text-purple-800 flex items-start gap-2">
                          <span className="bg-purple-200 text-purple-900 px-2 py-0.5 rounded text-xs font-semibold">
                            {idx + 1}
                          </span>
                          {scene}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300">
            <CardContent className="pt-6 text-center">
              <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-3" />
              <h3 className="text-xl font-bold text-green-900 mb-2">
                ¡Script Listo para Grabar!
              </h3>
              <p className="text-green-800 mb-4">
                Guardado automáticamente en tu historial
              </p>
              <Button onClick={() => setActiveTab('history')} variant="outline">
                Ver Historial de Scripts
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );

  const renderHistoryTab = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="w-5 h-5" />
          Historial de Scripts
        </CardTitle>
        <CardDescription>
          Todos los scripts que has generado: {history.length} scripts
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loadingHistory ? (
          <div className="text-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400 mx-auto" />
          </div>
        ) : history.length === 0 ? (
          <div className="text-center py-8">
            <Video className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600">Aún no has generado scripts</p>
            <Button onClick={() => setActiveTab('generate')} className="mt-4">
              Crear tu Primer Script
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {history.map((item, idx) => {
              const purposeLabels = {
                problem: 'Problema',
                solution: 'Solución',
                testimonial: 'Testimonial',
                educational: 'Educativo',
                authority: 'Autoridad',
                comparison: 'Comparación',
                opportunity: 'Oportunidad'
              };
              const styleLabels = {
                sinek: 'Inspiracional',
                hormozi: 'Lógico',
                belfort: 'Persuasivo'
              };
              
              return (
                <Card 
                  key={idx} 
                  className="hover:shadow-md transition-shadow cursor-pointer group"
                  onClick={() => {
                    setSelectedScript(item);
                    setShowDetailModal(true);
                  }}
                >
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded">
                            {item.duration}s
                          </span>
                          <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs font-semibold rounded">
                            {styleLabels[item.style] || item.style}
                          </span>
                          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded">
                            {purposeLabels[item.purpose] || item.purpose}
                          </span>
                          <span className="text-xs text-slate-500">
                            {item.created_at ? new Date(item.created_at).toLocaleDateString('es-ES', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            }) : ''}
                          </span>
                        </div>
                        {item.context && (
                          <p className="text-xs text-slate-500 mb-2 italic">"{item.context.substring(0, 80)}..."</p>
                        )}
                        <p className="text-sm text-slate-600 line-clamp-2">{item.script_text}</p>
                      </div>
                      <div className="flex gap-1 ml-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedScript(item);
                            setShowDetailModal(true);
                          }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopy(item.script_text || '');
                          }}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );

  // Detail Modal
  const renderDetailModal = () => {
    if (!showDetailModal || !selectedScript) return null;
    
    const purposeLabels = {
      problem: 'Problema',
      solution: 'Solución',
      testimonial: 'Testimonial',
      educational: 'Educativo',
      authority: 'Autoridad',
      comparison: 'Comparación',
      opportunity: 'Oportunidad'
    };
    const styleLabels = {
      sinek: 'Inspiracional',
      hormozi: 'Lógico',
      belfort: 'Persuasivo'
    };
    
    return (
      <div 
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={() => setShowDetailModal(false)}
      >
        <div 
          className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="bg-gradient-to-r from-red-600 to-orange-600 text-white p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Video className="w-6 h-6" />
                  Detalle del Script
                </h2>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <span className="px-2 py-1 bg-white/20 text-white text-xs font-semibold rounded">
                    {selectedScript.duration}s
                  </span>
                  <span className="px-2 py-1 bg-white/20 text-white text-xs font-semibold rounded">
                    {styleLabels[selectedScript.style] || selectedScript.style}
                  </span>
                  <span className="px-2 py-1 bg-white/20 text-white text-xs font-semibold rounded">
                    {purposeLabels[selectedScript.purpose] || selectedScript.purpose}
                  </span>
                  <span className="text-xs text-white/80">
                    {selectedScript.created_at ? new Date(selectedScript.created_at).toLocaleDateString('es-ES', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    }) : ''}
                  </span>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/20 rounded-full"
                onClick={() => setShowDetailModal(false)}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Modal Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
            {/* Context */}
            {selectedScript.context && (
              <div className="mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <h3 className="font-semibold text-sm text-slate-700 mb-2">📝 Contexto del Video:</h3>
                <p className="text-slate-600">{selectedScript.context}</p>
              </div>
            )}

            {/* Script Text */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-900">🎬 Script Completo:</h3>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleCopy(selectedScript.script_text || '')}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copiar Script
                </Button>
              </div>
              <div className="p-4 bg-white rounded-lg border-2 border-slate-200">
                <p className="text-slate-900 whitespace-pre-wrap leading-relaxed">
                  {selectedScript.script_text}
                </p>
              </div>
            </div>

            {/* Scene Descriptions */}
            {selectedScript.scene_descriptions && selectedScript.scene_descriptions.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-slate-900">🎥 Descripciones de Escenas:</h3>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleCopy(selectedScript.scene_descriptions.join('\n\n'))}
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copiar Escenas
                  </Button>
                </div>
                <div className="space-y-3">
                  {selectedScript.scene_descriptions.map((scene, idx) => (
                    <div key={idx} className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                      <div className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                          {idx + 1}
                        </span>
                        <p className="text-purple-900 flex-1">{scene}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Copy All Button */}
            <div className="flex gap-3 pt-4 border-t border-slate-200">
              <Button
                className="flex-1 bg-gradient-to-r from-red-600 to-orange-600"
                onClick={() => {
                  const fullContent = `SCRIPT DE VIDEO (${selectedScript.duration}s - ${styleLabels[selectedScript.style]})\n\n${selectedScript.context ? `Contexto: ${selectedScript.context}\n\n` : ''}GUIÓN:\n${selectedScript.script_text}\n\n${selectedScript.scene_descriptions?.length ? `ESCENAS:\n${selectedScript.scene_descriptions.map((s, i) => `${i + 1}. ${s}`).join('\n')}` : ''}`;
                  handleCopy(fullContent);
                }}
              >
                <Copy className="w-4 h-4 mr-2" />
                Copiar Todo
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowDetailModal(false)}
              >
                Cerrar
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-red-50 to-orange-50">
      {/* Page Header */}
      <div className="bg-white/80 border-b border-slate-200 px-4 sm:px-6 py-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => window.location.href = '/dashboard'}
            className="p-2 hover:bg-slate-100 rounded-full transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Generador de Scripts</h1>
            <p className="text-sm text-slate-600">Scripts profesionales para videos virales</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="flex gap-2 mb-6 overflow-x-auto">
          <Button
            onClick={() => setActiveTab('generate')}
            variant={activeTab === 'generate' ? 'default' : 'outline'}
          >
            <Video className="w-4 h-4 mr-2" />
            Generar Nuevo
          </Button>
          <Button
            onClick={() => setActiveTab('history')}
            variant={activeTab === 'history' ? 'default' : 'outline'}
          >
            <Calendar className="w-4 h-4 mr-2" />
            Historial ({history.length})
          </Button>
        </div>

        {activeTab === 'generate' && renderGenerateTab()}
        {activeTab === 'history' && renderHistoryTab()}
      </div>

      {/* Detail Modal */}
      {renderDetailModal()}
    </div>
  );
};

export default ScriptGeneratorNew;
